import React, {createRef, RefObject} from 'react'

import HintPopover from '../../hint-popover/hint-popover'
import InputInlineEditable from "../../../gd-components/input-inline-editable/input-inline-editable"
import EstimateAllocateUtils from "../../../gd/utils/estimate-allocate-utils"
import ControlButtonStandard from "../../../gd-components/buttons/control-button/control-button-standard"
import FormManager from "../../../gd-form/common/form-manager"
import {toggleClass} from "../../../gd/utils/dom-and-react/react-utils"

// @ts-ignore
import EstimatePickerPopover from "../popover/ui5/popover-ui5"

type Props = {
    className?          : string

    value?              : number
    childrenValue?      : number
    showSummary?        : boolean

    placeholder         : string
    hint?               : string
    disabled?           : boolean

    // form - if passed component will act as form element
    form?               : FormManager
    name?               : string

    // underline
    underlined?         : 'on-hover' | 'always' | 'never' | 'none'  // if set will show border bottom for the value

    // look and feel
    // text 13 = text normal text-12 = text small , 11 -> tiny
    renderValue         : 'text-normal' | 'text-13' | 'text-12' | 'text-11' | 'inline' | 'form-element' | 'form-input-hours' | 'control-button' | 'func', // render value view name
    renderValueFunc?    : func        // func for external render function if passwed used to render instead of default
    iconSize?           : number // fs-X or default 14px if unset (currently for 'control-button' only)

    deleteButton        : boolean        // if true will show delte button to reset date to null

    // popover options
    attachment          : string
    targetAttachment    : string
    offset?             : string

    // Callbacks
    onUserSelect        : func
    onTogglePopover?    : func // to track if any popover opened from this component
}

type State = {
    showPopover : boolean
    hours       : number
    minutes     : number
}

export default class EstimatePicker extends React.Component<Props, State> {

    private refFormInput: RefObject<any> = createRef()

    static defaultProps =  {
        placeholder         : "Estimate",

        deleteButton        : false,

        attachment          : "bottom center",
        targetAttachment    : "top center"
    }

    constructor(props: Props) {
        super(props)

        // same source of truth as UNSAFE_componentWillReceiveProps - the form when used as a form
        // element, the value prop otherwise. Missing the prop here left a freshly mounted picker
        // showing nothing until the next props update.
        let value
        if (this.props.form && this.props.name) {
            this.props.form.registerFormComponent(this.props.name, this)
            value = this.props.form.getValue(this.props.name)
        } else {
            value = this.props.value
        }

        const hours = value ? Math.floor(value / 60) : 0;
        const minutes = value ? value % 60 : 0;

        this.state = {
            showPopover : false,
            hours       : hours,
            minutes     : minutes
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props){
        // when form value changed and we re-render the whole thing
        if (nextProps.form && nextProps.name) {
            const formValue = nextProps.form.getValue(nextProps.name)
            if (formValue !== this._getValue()) {
                this._setValue(formValue)
            }
        } else {
            if (
                typeof nextProps.value !== 'undefined'
                && nextProps.value !== this._getValue()
            ) {
                this._setValue(nextProps.value)
            }
        }
    }

    _getValue = () => {
        return this.state.hours * 60 + this.state.minutes
    }

    _getRenderValue = () => {
        const value = this._getValue()
        return this.props.showSummary && !_.isNil(this.props.childrenValue) ? this.props.childrenValue + (value ? value : 0) : value
    }

    _setValue = (value: any) => {
        this.setState({
            hours   : value ? Math.floor(value / 60) : 0,
            minutes : value ? value % 60 : 0
        })
    }

    blur = () => {
        this.refFormInput.current?.blur()
    }

    togglePopover = () => {
        if (!this.props.disabled) {
            this.setState({
                showPopover: !this.state.showPopover
            }, ()=>{
                this.props.onTogglePopover?.(this.state.showPopover)
            })
        }
    }

    hidePopover = () => {
        this.setState({
            showPopover: false
        }, ()=>{
            this.props.onTogglePopover?.(this.state.showPopover)
        })
    }

    onDeleteClick = () => {
        this.props.onUserSelect?.(false);
        if (this.props.form && this.props.name) {
            this.props.form.onFormComponentValueChange(this.props.name,null);
        }

        this.hidePopover();
    }

    onFormInputHoursValueChange = (value: any) => {
        this.onSave(EstimateAllocateUtils.normalizeStringInputToMinutes(value));
    }

    onFormInputHoursValueImmediateChange = (value: any) => {
        toggleClass(this.refFormInput.current, "no-value", !value)
    }

    onSubmit = (result: any) => {
        let estimate = result.hours * 60 + result.minutes;
        if (estimate === 0) {
            estimate = null
        }

        this.props.onUserSelect?.(estimate);
        if (this.props.form && this.props.name) {
            this.props.form.onFormComponentValueChange(this.props.name, estimate);
        }

        this.hidePopover();
    }

    onSave = (value: any) => {
        const hours = value ? Math.floor(value / 60) : 0
        const minutes = value ? value % 60 : 0

        this.setState({
            hours   : hours,
            minutes : minutes
        }, ()=>{
            const newEstimateValue = this._getValue()
            this.props.onUserSelect?.(newEstimateValue)
            if (this.props.form && this.props.name) {
                this.props.form.onFormComponentValueChange(this.props.name,newEstimateValue)
            }
            this.hidePopover()
        })
    }

    renderPopover() {
        if (this.state.showPopover) {
            return (
                <EstimatePickerPopover
                    hours               = {this.state.hours}
                    minutes             = {this.state.minutes}
                    childrenValue       = {this.props.childrenValue}
                    attachment          = {this.props.attachment}
                    targetAttachment    = {this.props.targetAttachment}
                    offset              = {this.props.offset}
                    onClickOutside      = {()=>{this.hidePopover()}}
                    onSave              = {this.onSave}
                />
            )
        }

        return null
    }

    renderValue(value: any) {
        let valueLabel = this.props.placeholder
        if (value) valueLabel = gd.utils.minutesHumanize(value)

        const underlined = (this.props.disabled) ? 'underlined-never' : 'underlined-'+this.props.underlined;

        const curValueClasses = Classes('cur-value','pop-target', this.props.renderValue, underlined, {
            'no-value': !value
        })

        switch (this.props.renderValue) {
            case 'form-element':
            case 'text-normal':
            case 'text-13':
            case 'text-12':
            case 'text-11':
                return (
                    <div className  = {curValueClasses}
                         onClick    = {this.togglePopover}
                    >
                        {valueLabel}
                    </div>
                );

            case 'inline':

                return (
                    <div className  = {Classes(curValueClasses, "ui5-circle-selector")}
                         onClick    = {this.togglePopover}>
                        <div className="icon i5-time-estimate"/>

                        {!!value && (
                            <div className="ui5-txt-no-accent-small m-l-5">
                                {valueLabel}
                            </div>
                        )}

                    </div>
                )

            case 'form-input-hours':
                const hours = EstimateAllocateUtils.minutesToHours(value);
                return (
                    <InputInlineEditable
                        ref                 = {this.refFormInput}
                        className           = {curValueClasses}
                        placeholder         = "0"
                        value               = {hours > 0 ? hours.toString() : null}
                        type                = "number"
                        disabled            = {this.props.disabled}
                        onChange            = {this.onFormInputHoursValueChange}
                        onImmediateChange   = {this.onFormInputHoursValueImmediateChange}
                        autoSaveChanges     = {false}
                        autoSaveOnBlur      = {true}
                    />
                )

            case 'control-button':
                const iconClasses = Classes("i5-time-estimate", {
                    ['fs-'+this.props.iconSize]: !!this.props.iconSize
                })

                return (
                    <ControlButtonStandard
                        className   = "pop-target"
                        iconClass   = {iconClasses}
                        isDisabled  = {this.props.disabled}
                        onClick     = {this.togglePopover}
                    />
                )

            case 'func':
                return (
                    <div className  = {curValueClasses}
                         onClick    = {this.togglePopover}
                    >
                        {this.props.renderValueFunc?.(value)}
                    </div>
                );
        }
    }

    render() {
        const value = this._getRenderValue()

        const classes = Classes("gd-select-estimate", this.props.className, 'render-'+this.props.renderValue, {
            'disabled'      : this.props.disabled,
            'not-selected'  : !value,
            'open'          : this.state.showPopover,
        })

        const showSummary =  !!value && this.props.showSummary

        return (
            <div className={classes}>
                {showSummary &&
                    <div className="estimate-icon ui5-ico-12 i5-summary-symbol m-r-5"/>}

                {this.renderValue(value)}

                {!this.props.disabled && this.props.deleteButton &&
                    <div className  = "i5-delete-circle-solid"
                         onClick    = {this.onDeleteClick}
                    />}

                {this.renderPopover()}
                {this.props.hint && !this.state.showPopover &&
                    <HintPopover parent={this} hint={this.props.hint}/>}
            </div>
        );
    }
}
